// admin/src/i18n.ts
//
// Client-side translation helper for the Timestop backoffice SPA.
// Read locale strings from admin/locales/{iso}.json by dotted key,
// substitute placeholders, and return the result. Mirrors the
// PHP t() helper in admin/api/I18n.php so the two sides share
// the same .json files and the same key conventions (e.g.
// 'nav.dashboard', 'tab_settings.save_confirm_title').
//
// The locale .json files are static assets served by the web
// server (Apache serves admin/locales/*.json directly). We use
// raw fetch() rather than the apiFetch wrapper because:
//   1. There's no PHP endpoint involved -- the file is served
//      as a static asset by the web server.
//   2. No CSRF token is required (GET-only reads).
//   3. No auth envelope -- the locale files are public.
//
// Lifecycle:
//   1. main.ts calls loadLocales([...]) at boot, which fetches
//      all three .json files in parallel and stores the result
//      in the module-level `catalog` map. EN is required (it's
//      the fallback); CZ and PL are best-effort.
//   2. main.ts calls setActiveLang(state.currentUser.lang)
//      after the first /api/me round-trip, so the page chrome
//      renders in the right language on first paint.
//   3. user-menu.ts's setLanguage() updates
//      state.currentUser.lang, POSTs to /api/change_my_lang,
//      and calls setActiveLang(lang) to re-render.
//
// Re-render strategy:
//   We don't have a virtual DOM. After a language change, we
//   need to redraw the active page and any open modal. The
//   bundle already exposes:
//     - navigate(n)          -- repaints page n
//     - renderTab(n)         -- repaints modal tab n
//     - clearRangedState()   -- wipes the Ranged tab's per-terminal
//                                cache so the next paint reloads
//   The two subscribers installed by main.ts (one for the page,
//   one for the modal) cover every visible string. A page refresh
//   is the nuclear option if a re-render needs to redraw the
//   static shell (see user-menu.ts: setLanguage falls back to
//   location.reload() if no re-render triggers are wired up).
//
// Placeholder convention:
//   Templates use {name} where `name` is one of:
//     - {n} / {s1} / {s2} / {s3} (BoOps printf-style, used by
//       the logs catalog)
//     - {when} / {name} / {date} / {terminal} / {count} (UI
//       strings, used by the dashboard / modals / etc.)
//   The same template can mix both styles. Missing keys in the
//   value map become empty strings; the key itself is returned
//   if neither the active locale nor the EN fallback has the
//   key, so a missing translation surfaces visibly in QA.
//
// Trust boundary:
//   The TS t() does NOT html-escape its output. Renderers
//   embed the result in innerHTML (a template literal) or
//   textContent, and any escape step would either be a no-op
//   for textContent or a footgun for innerHTML. Translators
//   should not introduce HTML; reviewers should grep for '<'
//   in the .json files before publishing.

export type Lang = 'en' | 'cz' | 'pl';
export const SUPPORTED_LANGS: Lang[] = ['en', 'cz', 'pl'];

/** Per-language dictionary: full parsed JSON object. */
const catalog: Partial<Record<Lang, any>> = {};

// The EN catalog is the source of truth for every key, so we
// import it at build time. This guarantees the t() lookup never
// returns the raw dotted key on first render — the
// previously-async fetch could leave the catalog empty for the
// first paint, and the operator would briefly see literal
// "dashboard.terminals_suffix" / "common.cash" baked into the
// JSX. With the import below, the EN fallback is always present
// synchronously.
//
// CZ / PL are best-effort runtime fetches: the lookup still falls
// through to EN if the operator's active locale hasn't loaded
// yet, so the page stays readable instead of flashing raw keys.
//
// JSON imports are a Vite-native feature; the EN catalog ships
// inline in the bundle (typescript `resolveJsonModule` is enabled
// by default in vite's tsconfig).
import enCatalog from '../locales/en.json';
catalog.en = enCatalog;

/** Currently active locale code. Defaults to 'en'. */
let active: Lang = 'en';

/** Re-render subscribers. Called after setActiveLang(). */
type LangSubscriber = (lang: Lang) => void;
const subscribers: Set<LangSubscriber> = new Set();

/** Test/debug: was the catalog populated for this language? */
export function isLangLoaded(lang: Lang): boolean {
    return !!catalog[lang];
}

/** Read the currently active language. */
export function activeLang(): Lang {
    return active;
}

/**
 * Subscribe to language changes. The callback fires AFTER
 * setActiveLang() has updated the active-lang pointer. Pass
 * an unsubscribe function in return so the caller can clean
 * up on page navigation if needed (in practice the subscribers
 * are installed once at boot and live for the page's lifetime).
 */
export function onLangChange(cb: LangSubscriber): () => void {
    subscribers.add(cb);
    return () => subscribers.delete(cb);
}

/**
 * t() — look up a dotted key in the active locale (with EN
 * fallback) and substitute placeholders.
 *
 * @param key   Dotted path, e.g. 'nav.dashboard'.
 * @param args  Optional map of placeholder values keyed by
 *              placeholder name (e.g. {when: 'today'}).
 * @returns     The translated string, or the key itself
 *              when the lookup misses both the active locale
 *              and the EN fallback (so a missing translation
 *              surfaces visibly in QA).
 */
export function t(key: string, args?: Record<string, string | number | undefined | null>): string {
    const str = resolve(key);
    if (str === null) return key;
    return substitute(str, args as Record<string, string | number> | undefined);
}

/**
 * Convenience: t() with a no-args call. Most renderers call
 * `t('nav.dashboard')` without placeholders, so this is the
 * 90% case.
 */
export const ti = (key: string): string => t(key);

/**
 * Resolve the URL to fetch a locale file from.
 *
 * In production (no ?devon=p), the relative URL is served by the
 * same Apache that serves the page — canonical path
 * /admin/locales/{lang}.json.
 *
 * In dev mode (?devon=p or ?devon=h), the page is still served
 * by Apache, but the JS bundle (and its assets) come from a
 * Vite dev server tunneled through Cloudflared. The relative
 * URL would otherwise hit the LIVE server's stale locale
 * cache, missing every key we just added via HMR. The
 * injected window.__timestop_localeBase (set by the inline
 * bootstrap script in admin/index.php when ?devon=* is on)
 * pins us to the Vite origin so the freshly-edited files
 * are visible.
 *
 * If window.__timestop_localeBase is unset (e.g. production
 * build, or an iframe that lost the global), fall back to
 * the relative URL so the live server's locales win.
 */
function localeUrl(lang: Lang): string {
    // Priority: window.__timestop_localeBase (PHP-injected) >
    // localStorage.__timestop_localeBase (sticky, set by the dev
    // user once for the whole HMR session) > URL param
    // ?localeBase=... (one-shot override) > URL hash
    // #localeBase=... (one-shot override, survives when the
    // browser sandbox strips query params) > import.meta.url
    // origin (when the bundle is being served from a Vite
    // tunnel, fetch the locales from the same Vite tunnel so
    // the dev's HMR-edited JSON files are visible) > relative
    // URL (production / canonical).
    let base = (window as unknown as { __timestop_localeBase?: string }).__timestop_localeBase;
    if (typeof base !== 'string' || base.length === 0) {
        try {
            const ls = window.localStorage?.getItem('__timestop_localeBase');
            if (typeof ls === 'string' && ls.length > 0) base = ls;
        } catch { /* localStorage may be blocked */ }
    }
    if ((typeof base !== 'string' || base.length === 0)
        && typeof window.location !== 'undefined'
        && typeof window.location.search === 'string') {
        try {
            const usp = new URLSearchParams(window.location.search);
            const qb = usp.get('localeBase');
            if (typeof qb === 'string' && qb.length > 0) base = qb;
        } catch { /* URLSearchParams may not be available in old browsers */ }
    }
    if ((typeof base !== 'string' || base.length === 0)
        && typeof window.location !== 'undefined'
        && typeof window.location.hash === 'string') {
        const hm = window.location.hash.match(/localeBase=([^&]+)/);
        if (hm && typeof hm[1] === 'string' && hm[1].length > 0) {
            try { base = decodeURIComponent(hm[1]); } catch { base = hm[1]; }
        }
    }
    // Final fallback: if the bundle was loaded from a Vite
    // dev origin (anything *.vadilab.org — the office and
    // home Cloudflare tunnels), assume the locales live at
    // the same Vite origin. This makes dev mode "just work"
    // with a hard reload, no PHP or query-string setup
    // required. We use import.meta.url rather than
    // window.location so the detection tracks the BUNDLE's
    // origin, not the page's (the page is still served from
    // testing.gmcoin.cz even in dev mode).
    if ((typeof base !== 'string' || base.length === 0)) {
        try {
            // import.meta.url is a Vite-rewritten module URL
            // like https://gmcoin.vadilab.org/src/i18n.ts.
            // We parse out the origin and check whether it
            // looks like a dev tunnel.
            const u = new URL(import.meta.url);
            if (/vadilab\.org$/i.test(u.hostname)) {
                base = u.protocol + '//' + u.host;
            }
        } catch { /* import.meta.url may be missing in some bundles */ }
    }
    if (typeof base === 'string' && base.length > 0) {
        return base.replace(/\/$/, '') + '/locales/' + lang + '.json';
    }
    return 'locales/' + lang + '.json';
}

/**
 * Load a locale file from admin/locales/{lang}.json. Returns
 * the parsed object on success, null on failure (the file is
 * missing or malformed). Callers should always load 'en' first
 * and treat it as a hard requirement; other langs are
 * best-effort.
 */
export async function loadLocale(lang: Lang): Promise<any | null> {
    try {
        const res = await fetch(localeUrl(lang), {
            credentials: 'same-origin',
            cache: 'no-cache',   // translators edit & HMR; never serve stale
        });
        if (!res.ok) return null;
        const data = await res.json();
        if (!data || typeof data !== 'object') return null;
        catalog[lang] = data;
        return data;
    } catch (e) {
        // Network error or 404. Keep whatever we have for this lang.
        return null;
    }
}

/**
 * Load all supported locales in parallel. EN is the boot
 * requirement; the others can fail without breaking the page
 * (the active lang is whatever setActiveLang picks, and EN is
 * the default).
 */
export async function loadLocales(langs: Lang[] = SUPPORTED_LANGS): Promise<void> {
    await Promise.all(langs.map(loadLocale));
}

/**
 * Set the active language and notify all subscribers. Does NOT
 * fetch the .json file -- the caller is expected to have called
 * loadLocale() first. Idempotent: setting the same lang twice
 * is a no-op.
 */
export function setActiveLang(lang: Lang): void {
    if (lang === active) return;
    active = lang;
    for (const cb of subscribers) {
        try { cb(lang); } catch (e) { /* don't let one bad subscriber break the rest */ }
    }
}

// ============================================================
// internals
// ============================================================

/** Look up a dotted key in (active) then (en fallback). */
function resolve(key: string): string | null {
    const cur = walk(catalog[active], key);
    if (typeof cur === 'string' && cur.length > 0) return cur;
    if (active !== 'en') {
        const fb = walk(catalog.en, key);
        if (typeof fb === 'string' && fb.length > 0) return fb;
    }
    return null;
}

/** Walk a dotted path through a nested object. */
function walk(data: any, key: string): any {
    if (!data || typeof data !== 'object') return null;
    let cur: any = data;
    for (const seg of key.split('.')) {
        if (cur == null || typeof cur !== 'object' || !(seg in cur)) return null;
        cur = cur[seg];
    }
    return cur;
}

/**
 * Substitute placeholders in a translated string. Supports two
 * shapes:
 *
 *   1. Simple {name} substitution — the most common case
 *      (e.g. "Hello {name}", { count: 4 } → "Hello 4").
 *
 *   2. ICU-lite plural syntax: {name, plural,
 *      =0{no items} one{1 item} few{N items} many{N items}
 *      other{N items}}. The CLDR plural categories (=0, zero,
 *      one, two, few, many, other) are matched in order; the
 *      first that matches the count is used. If no plural
 *      clause matches, the literal "other" is used (and if
 *      that is also missing, the entire {name, plural, ...}
 *      span is left in place as a visible debug aid — the
 *      developer sees a literal "{count, plural, ...}" in
 *      the UI rather than a silently-wrong string).
 *
 * The plural rules we ship:
 *   - one:   count === 1
 *   - =0:    count === 0
 *   - other: catch-all (must be present in every plural
 *            string or substitution falls through to the
 *            debug-keep)
 *
 * English treats all counts ≥ 2 as "other" so {count,
 * plural, one{x} other{xs}} is enough. Czech needs
 * {count, plural, one{x} few{xs} many{xs} other{xs}} (CLDR
 * rules: 1 → one, 2-4 → few, 0+5+ → many, fractional → other).
 * Polish has the same set of CLDR categories as Czech.
 * The t() helper does NOT compute CLDR categories itself
 * (that needs Intl.PluralRules and the full Unicode CLDR
 * database) — the locale value just has to ship the right
 * set of branches for the language. This is the same shape
 * ICU MessageFormat uses; the difference is we don't
 * support nested select/variables inside the plural arms.
 */
function substitute(template: string, args?: Record<string, string | number | undefined | null>): string {
    if (!args) return template;
    // Phase 1: ICU-lite plural — {name, plural, ...branches...}
    // The body is brace-balanced (e.g. "one{X} other{Xs}"),
    // so we can't use a simple [^}]* regex — we walk through
    // the string manually looking for the matching closing
    // brace of the plural block.
    let out = '';
    let i = 0;
    while (i < template.length) {
        const ch = template[i];
        if (ch !== '{') { out += ch; i++; continue; }
        // Find the matching closing brace, respecting nesting.
        let depth = 0;
        let j = i;
        for (; j < template.length; j++) {
            if (template[j] === '{') depth++;
            else if (template[j] === '}') {
                depth--;
                if (depth === 0) { j++; break; }
            }
        }
        const block = template.slice(i, j);
        const plural = block.match(/^\{([a-zA-Z_][a-zA-Z0-9_]*)\s*,\s*plural\s*,([\s\S]*)\}$/);
        if (plural) {
            const name = plural[1];
            const body = plural[2];
            if (name in args) {
                const count = Number(args[name]);
                const branches = parsePluralBranches(body);
                out += pickPluralBranch(branches, count, block);
            }
            // else: drop the whole plural block (matches
            // the pre-fix behavior of "missing var -> ''")
        } else {
            // Not a plural. Try simple {name} substitution;
            // if it matches a known arg, substitute; else
            // pass through unchanged.
            const simple = block.match(/^\{([a-zA-Z_][a-zA-Z0-9_]*)\}$/);
            if (simple && simple[1] in args) {
                out += String(args[simple[1]]);
            } else {
                out += block;
            }
        }
        i = j;
    }
    return out;
}

/** Parse "one{X} few{Xs} other{Xs}" into a {category: text} map. */
function parsePluralBranches(body: string): Record<string, string> {
    const branches: Record<string, string> = {};
    // Match category tokens like "one", "other", "=0" followed
    // by a {...} block. The body can contain nested braces
    // inside the value, so we use a brace-depth counter.
    let i = 0;
    while (i < body.length) {
        // Skip whitespace.
        while (i < body.length && /\s/.test(body[i])) i++;
        if (i >= body.length) break;
        // Read the category token (letters / digits / =).
        const catStart = i;
        while (i < body.length && /[a-zA-Z0-9=]/.test(body[i])) i++;
        const cat = body.slice(catStart, i).trim();
        if (!cat) break;
        // Skip whitespace, expect `{`.
        while (i < body.length && /\s/.test(body[i])) i++;
        if (body[i] !== '{') break;
        // Read the brace-balanced value.
        let depth = 0;
        const valStart = i;
        for (; i < body.length; i++) {
            if (body[i] === '{') depth++;
            else if (body[i] === '}') {
                depth--;
                if (depth === 0) {
                    i++;
                    break;
                }
            }
        }
        const val = body.slice(valStart + 1, i - 1);
        branches[cat] = val;
    }
    return branches;
}

/**
 * Minimal CLDR plural category selection. The locale value is
 * the source of truth for the language's branches; this just
 * picks the right one for the count. Implements the four
 * categories Timestop's three languages (EN/CZ/PL) need:
 *
 *   one  — count === 1                 (EN: 1; CZ/PL: 1)
 *   few  — count in 2..4               (CZ/PL: 2-4)
 *   many — count in 0, 5..∞            (CZ/PL: 0 + 5+)
 *   other— catch-all (must exist)
 *
 * Anything more exotic (fractions, compact decimals, etc.) is
 * deferred to a real Intl.PluralRules implementation when a
 * locale needs it. The "one" form fires for count === 1 in all
 * three languages; "few" fires for 2..4 (the CZ/PL "small
 * plural" range); "many" covers the rest of the integers.
 * Note: we intentionally treat 0 as "many" in CZ/PL to match
 * the CLDR rule (CZK: "0" → many, "1" → one, "2-4" → few).
 */
function pickPluralBranch(
    branches: Record<string, string>,
    count: number,
    fallback: string,
): string {
    if (!isFinite(count)) return branches.other ?? fallback;
    // Explicit numeric match: =0, =1, =2, etc. Always wins
    // — useful when the locale wants a special form for an
    // exact count (e.g. "=0{no items}" or "=5{almost}").
    const eqKey = '=' + count;
    if (branches[eqKey] !== undefined) {
        return branches[eqKey].replace(/#/g, String(count));
    }
    // CLDR-style categories. Order matters: more-specific
    // categories first.
    let chosen: string | undefined;
    if (count === 1 && branches.one !== undefined) chosen = branches.one;
    else if (count >= 2 && count <= 4 && branches.few !== undefined) chosen = branches.few;
    else if ((count === 0 || count >= 5) && branches.many !== undefined) chosen = branches.many;
    else if (branches.two !== undefined && count === 2) chosen = branches.two;
    else if (branches.zero !== undefined && count === 0) chosen = branches.zero;
    else if (branches.other !== undefined) chosen = branches.other;
    if (chosen === undefined) return fallback; // keep literal so the developer sees the gap
    // CLDR `#` inside a branch = the count itself (e.g.
    // "{count, plural, one{# entry} other{# entries}}").
    return chosen.replace(/#/g, String(count));
}
